import React, { useState, forwardRef, useEffect, useMemo } from 'react';
import { Calendar as CalendarIcon, Shield, AlertCircle as _AlertCircle, Check, AlertCircle } from 'lucide-react';
import Calendar from '../shared/Calendar';
import BookingCalendar from '../calendar/BookingCalendar';
import ConsecutiveDayStrategy from '../calendar/strategies/ConsecutiveDayStrategy';
import BottomSheet from '../shared/BottomSheet';
import StillInterestedModal from '../shared/StillInterestedModal';
import HoldExpiredModal from '../shared/HoldExpiredModal';
import AlternativeDatesPanel from '../shared/AlternativeDatesPanel';
import { useTheme } from '../../hooks/useTheme';
import { useBookingHold } from '../../hooks/useBookingHold';
import { useStalePageProtection } from '../../hooks/useStalePageProtection';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatCentsToDollars } from '../../utils/formHelpers';

// Feature flag for gradual rollout of new booking calendar
const USE_NEW_BOOKING_CALENDAR = import.meta.env.VITE_FEATURE_NEW_BOOKING_CALENDAR === 'true';

// Feature flag for consecutive multi-date booking (SCRUM-708)
const USE_CONSECUTIVE_BOOKING = import.meta.env.VITE_FEATURE_CONSECUTIVE_BOOKING === 'true';

/**
 * BookingCard Component
 * Sticky booking card with date selection, guest count, pricing, and hold management
 *
 * State can be controlled externally or managed internally (for backward compatibility)
 */
const BookingCard = forwardRef(function BookingCard({
  guideId,
  guideServiceId,
  pricePerPerson = 899,
  serviceFee = 89,
  minGuests = 1,
  maxGuests = 8,
  unavailableDates = [],
  limitedDates = [],
  experience = null,
  isSticky = true,
  // Controlled state props (optional - for lifting state up)
  selectedDate: externalSelectedDate,
  onSelectedDateChange,
  guests: externalGuests,
  onGuestsChange,
  showCalendar: externalShowCalendar,
  onShowCalendarChange,
  onReserve,
  // Hold-related props (optional - for sharing hold state)
  hold: externalHold,
  holdLoading: externalHoldLoading,
  holdError: externalHoldError,
  onCreateHold,
  onReleaseHold
}, ref) {
  // Use external state if provided, otherwise use internal state
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const [internalGuests, setInternalGuests] = useState(2);
  const [internalShowCalendar, setInternalShowCalendar] = useState(false);

  const selectedDate = externalSelectedDate !== undefined ? externalSelectedDate : internalSelectedDate;
  const guests = externalGuests !== undefined ? externalGuests : internalGuests;
  const showCalendar = externalShowCalendar !== undefined ? externalShowCalendar : internalShowCalendar;

  const setSelectedDate = onSelectedDateChange || setInternalSelectedDate;
  const setGuests = onGuestsChange || setInternalGuests;
  const setShowCalendar = onShowCalendarChange || setInternalShowCalendar;
  const [optimisticPrice, setOptimisticPrice] = useState(null);
  const [priceVerified, setPriceVerified] = useState(false);
  const [feedbackStage, setFeedbackStage] = useState('idle'); // idle, immediate, processing, complete
  const [validationErrors, setValidationErrors] = useState({ date: false, guests: false });
  const theme = useTheme();
  const isMobile = useIsMobile();

  // Build consecutive day strategy when feature flag + experience config match (SCRUM-708)
  const consecutiveStrategy = useMemo(() => {
    if (USE_CONSECUTIVE_BOOKING && experience?.duration_type === 'single_day') {
      return new ConsecutiveDayStrategy({
        maxDays: experience?.max_consecutive_days || 7,
        minBookingHours: experience?.bookingLeadTimeHours || experience?.booking_lead_time_hours || 24,
        startDayRestrictions: experience?.start_day_restrictions || null
      });
    }
    return undefined;
  }, [experience?.duration_type, experience?.max_consecutive_days,
      experience?.bookingLeadTimeHours, experience?.booking_lead_time_hours,
      experience?.start_day_restrictions]);

  // Use external hold if provided, otherwise use internal hook
  const internalHook = useBookingHold({ guideId, guideServiceId });

  const hold = externalHold !== undefined ? externalHold : internalHook.hold;
  const holdLoading = externalHoldLoading !== undefined ? externalHoldLoading : internalHook.loading;
  const holdError = externalHoldError !== undefined ? externalHoldError : internalHook.error;
  const createHold = onCreateHold || internalHook.createHold;
  const releaseHold = onReleaseHold || internalHook.releaseHold;

  // These are always from internal hook (not lifted yet)
  const {
    timeRemaining: _timeRemaining,
    suggestions,
    validateHold,
    trackProgressAction,
    showStillInterestedModal,
    showExpiredModal,
    handleExtendFromModal,
    handleReleaseFromModal,
    handleReloadFromExpired,
    handleContinueBrowsing,
  } = internalHook;

  // Stale page protection: auto-reload after 1 hour of idle time
  const { getRestoredState } = useStalePageProtection({
    idleThreshold: 60 * 60 * 1000, // 1 hour
    preserveState: {
      selectedDate,
      guests,
      showCalendar,
      scrollPosition: typeof window !== 'undefined' ? window.scrollY : 0,
    },
  });

  // Restore state after reload
  useEffect(() => {
    const restored = getRestoredState();
    if (restored) {
      if (restored.selectedDate) {setSelectedDate(restored.selectedDate);}
      if (restored.guests) {setGuests(restored.guests);}
      if (restored.showCalendar) {setShowCalendar(restored.showCalendar);}
      if (restored.scrollPosition && typeof window !== 'undefined') {
        window.scrollTo(0, restored.scrollPosition);
      }
    }
  }, [getRestoredState, setSelectedDate, setGuests, setShowCalendar]);

  // Sync selectedDate with restored hold
  useEffect(() => {
    if (hold && hold.date && !selectedDate) {
      // Format date from ISO (2025-11-13 or 2025-11-13T00:00:00.000Z)
      // to Calendar format (November 2025 13)
      // IMPORTANT: Parse as local date to avoid timezone shift
      const dateStr = hold.date.split('T')[0]; // Get YYYY-MM-DD part
      const [year, month, day] = dateStr.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day); // month is 0-indexed

      const monthName = dateObj.toLocaleDateString('en-US', { month: 'long' });
      const formattedDate = `${monthName} ${year} ${day}`;

      // Use the controlled state setter (calls parent's setSelectedDate if provided)
      setSelectedDate(formattedDate);
    }
  }, [hold, selectedDate, setSelectedDate]);

  // Optimistic price calculation (immediate) with background verification
  const calculateOptimisticPrice = () => {
    return pricePerPerson * guests + serviceFee;
  };

  const totalPrice = optimisticPrice !== null && !priceVerified
    ? optimisticPrice
    : calculateOptimisticPrice();
  const subtotal = pricePerPerson * guests;

  // Expose internal state to parent via ref
  React.useImperativeHandle(ref, () => ({
    hold,
    selectedDate,
    guests,
    totalPrice,
    holdLoading
  }), [hold, selectedDate, guests, totalPrice, holdLoading]);

  // Optimistic price update on guest change with background verification
  useEffect(() => {
    // Immediately show optimistic price
    const newPrice = pricePerPerson * guests + serviceFee;
    setOptimisticPrice(newPrice);
    setPriceVerified(false);

    // Background verification after 500ms delay
    const verifyTimeout = setTimeout(() => {
      // In a real implementation, this would call an API to verify pricing
      // For now, we just mark it as verified
      setPriceVerified(true);
      setOptimisticPrice(null); // Clear optimistic price, use calculated
    }, 500);

    return () => clearTimeout(verifyTimeout);
  }, [guests, selectedDate, pricePerPerson, serviceFee]);

  // Track progress actions for hold extension (meaningful actions only)
  useEffect(() => {
    if (hold && guests !== 2) {
      trackProgressAction(1, 'field_completed'); // Guest count is a completed field
    }
  }, [guests, hold, trackProgressAction]);

  /**
   * Convert Date object to old calendar string format ("November 2025 15")
   * For compatibility with existing BookingCard code
   * Note: Format must be parseable by JavaScript Date constructor
   */
  const formatDateToString = (date) => {
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    const day = date.getDate();
    // Use "Month Day, Year" format which is parseable by new Date()
    return `${month} ${day}, ${year}`;
  };

  /**
   * Parse old calendar string format to Date object
   * Handles multiple formats:
   * - "November 2025 15" (old format without comma)
   * - "November 15, 2025" (new format with comma)
   */
  const parseDateFromString = (dateString) => {
    if (!dateString) {return null;}
    try {
      // Check if in YYYY-MM-DD format (from new calendar)
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }

      // Try parsing directly first (works with "November 15, 2025" format)
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        return dateObj;
      }

      // Fallback: Parse "November 2025 15" format (old format)
      const parts = dateString.split(' ');
      if (parts.length === 3) {
        const month = parts[0];
        // Check if second part is year (4 digits) or day
        if (parts[1].length === 4) {
          // Old format: "November 2025 15"
          const year = parseInt(parts[1]);
          const day = parseInt(parts[2].replace(',', ''));
          return new Date(`${month} ${day}, ${year}`);
        } else {
          // New format: "November 15, 2025"
          const day = parseInt(parts[1].replace(',', ''));
          const year = parseInt(parts[2]);
          return new Date(`${month} ${day}, ${year}`);
        }
      }
    } catch (error) {
      console.warn('[BookingCard] Error parsing date string:', dateString, error);
    }
    return null;
  };

  const handleDateSelect = async (date) => {
    // Stage 1: Immediate feedback (0ms) - Optimistic UI update
    setFeedbackStage('immediate');
    setSelectedDate(date);
    setShowCalendar(false);
    // Clear date validation error when date is selected
    setValidationErrors(prev => ({ ...prev, date: false }));

    // Stage 2: Processing indicator (50ms) - Show we're working on it
    const stage2Timer = setTimeout(() => {
      setFeedbackStage('processing');
    }, 50);

    // Release existing hold before creating new one
    if (hold) {
      await releaseHold();
    }

    // Convert date string to YYYY-MM-DD format if needed
    // New calendar passes YYYY-MM-DD directly, old calendar passes other formats
    let formattedDate;
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      // Already in YYYY-MM-DD format (from new calendar)
      formattedDate = date;
    } else {
      // Convert from other format (from old calendar)
      const dateObj = new Date(date);
      formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
    }

    // Stage 3: API call (background)
    const result = await createHold(formattedDate);

    clearTimeout(stage2Timer);

    // Stage 4: Complete (200ms minimum for smooth transition)
    setTimeout(() => {
      setFeedbackStage('complete');

      // Return to idle after completion
      setTimeout(() => {
        setFeedbackStage('idle');
      }, 1000);
    }, 200);

    if (!result.success && result.suggestions) {
      // Show alternative dates in UI
      console.log('Alternative dates available:', result.suggestions);
    }
  };

  const handleReserve = async () => {
    // If external onReserve handler provided, use it
    if (onReserve) {
      onReserve({ selectedDate, guests, hold, totalPrice });
      return;
    }

    // Otherwise, use default internal logic
    // Validation: Check if date is selected
    if (!selectedDate || !hold) {
      setValidationErrors(prev => ({ ...prev, date: true }));
      setShowCalendar(true); // Auto-open calendar on validation failure
      return;
    }

    // Validate hold before proceeding (checks expiration and stale page)
    const isValid = await validateHold();
    if (!isValid) {
      // Hold validation failed (expired or stale page)
      // Error message is set by validateHold hook
      setSelectedDate(null);
      return;
    }

    // Track progress action before proceeding to checkout
    trackProgressAction(2, 'step_advanced'); // User advancing to checkout step

    // TODO: Navigate to checkout page with hold ID
    console.log('Proceeding to checkout', {
      holdId: hold.id,
      selectedDate,
      guests,
      totalPrice,
    });
  };

  const _handleCancelHold = () => {
    releaseHold();
    setSelectedDate(null);
  };

  // Wrapper for modal release that also clears selected date
  const handleReleaseFromModalWithClear = () => {
    handleReleaseFromModal();
    setSelectedDate(null);
  };

  const handleContactGuide = () => {
    // TODO: Navigate to contact guide page or open contact modal
    console.log('Contact guide requested');
  };

  const stickyClass = isSticky ? 'sticky top-24 mt-2' : '';

  return (
    <div ref={ref} className={`${stickyClass} bg-white rounded-xl p-6 shadow-lg border border-gray-200 ${feedbackStage === 'processing' ? 'ring-2 ring-rose-200' : ''}`}>
      {/* Feedback Stage Indicator */}
      {feedbackStage === 'processing' && (
        <div className="mb-4 p-2 bg-rose-50 border border-rose-200 rounded-lg animate-pulse">
          <div className="text-xs text-rose-700 text-center">Checking availability...</div>
        </div>
      )}
      {feedbackStage === 'complete' && (
        <div className="mb-4 p-2 bg-green-50 border border-green-200 rounded-lg">
          <div className="text-xs text-green-700 text-center flex items-center justify-center gap-1">
            Date selected <Check className="w-3 h-3" />
          </div>
        </div>
      )}

      {/* Error Message */}
      {holdError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-900">
              <div className="font-semibold mb-1">Unable to Reserve Date</div>
              <div>{holdError}</div>
            </div>
          </div>
        </div>
      )}

      {/* Alternative Dates Panel */}
      {suggestions.length > 0 && (
        <div className="mb-4">
          <AlternativeDatesPanel
            suggestions={suggestions}
            onSelectDate={handleDateSelect}
            onContactGuide={handleContactGuide}
            basePrice={pricePerPerson}
          />
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold">{formatCentsToDollars(totalPrice)}</span>
          <span className="text-gray-600">total</span>
        </div>
        <div className="text-sm text-gray-600 mt-1">
          {formatCentsToDollars(pricePerPerson)} per person + {formatCentsToDollars(serviceFee)} service fee
        </div>
        {minGuests > 1 && (
          <div className="text-sm text-amber-700 mt-1 font-medium">
            Minimum {minGuests} guests required
          </div>
        )}
      </div>

      <div className="space-y-3 mb-4">
        <div className={`p-3 border rounded-lg ${validationErrors.date ? 'border-red-500 border-2' : ''}`}>
          <button
            type="button"
            onClick={() => setShowCalendar(!showCalendar)}
            className="w-full text-left"
          >
            <div className="text-xs font-semibold text-gray-900 mb-1">DATES</div>
            <div className="text-sm text-gray-600 flex items-center justify-between">
              <span>{selectedDate || 'Select dates'}</span>
              <CalendarIcon className="w-4 h-4" />
            </div>
          </button>

          {validationErrors.date && (
            <div className="mt-2 text-xs text-red-600 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              <span>Please select a date to continue</span>
            </div>
          )}

          {/* Desktop: Inline calendar */}
          {!isMobile && showCalendar && (
            USE_NEW_BOOKING_CALENDAR ? (
              <BookingCalendar
                guideId={guideId}
                guideServiceId={guideServiceId}
                selectedDate={selectedDate ? parseDateFromString(selectedDate) : null}
                onDateSelect={(date) => {
                  // Convert Date object directly to YYYY-MM-DD format (avoiding double conversion)
                  const yyyy = date.getFullYear();
                  const mm = String(date.getMonth() + 1).padStart(2, '0');
                  const dd = String(date.getDate()).padStart(2, '0');
                  const dateString = `${yyyy}-${mm}-${dd}`;
                  handleDateSelect(dateString);
                }}
                experience={experience}
                strategy={consecutiveStrategy}
              />
            ) : (
              <Calendar
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                guideId={guideId}
                guideServiceId={guideServiceId}
                unavailableDates={unavailableDates}
                limitedDates={limitedDates}
              />
            )
          )}
        </div>

        {/* Mobile: Bottom sheet calendar */}
        {isMobile && (
          <BottomSheet
            isOpen={showCalendar}
            onClose={() => setShowCalendar(false)}
            title="Select Date"
            initialHeight={65}
            expandedHeight={90}
          >
            {USE_NEW_BOOKING_CALENDAR ? (
              <BookingCalendar
                guideId={guideId}
                guideServiceId={guideServiceId}
                selectedDate={selectedDate ? parseDateFromString(selectedDate) : null}
                onDateSelect={(date) => handleDateSelect(formatDateToString(date))}
                experience={experience}
                strategy={consecutiveStrategy}
              />
            ) : (
              <Calendar
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                guideId={guideId}
                guideServiceId={guideServiceId}
                unavailableDates={unavailableDates}
                limitedDates={limitedDates}
              />
            )}
          </BottomSheet>
        )}

        <div className="p-3 border rounded-lg">
          <div className="text-xs font-semibold text-gray-900 mb-2">GUESTS</div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Adults</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setGuests(Math.max(minGuests, guests - 1))}
                className={`w-8 h-8 border rounded-full hover:${theme.borderPrimary} transition`}
              >
                -
              </button>
              <span className="font-semibold w-8 text-center">{guests}</span>
              <button
                type="button"
                onClick={() => setGuests(Math.min(maxGuests, guests + 1))}
                className={`w-8 h-8 border rounded-full hover:${theme.borderPrimary} transition`}
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={handleReserve}
        disabled={!hold || holdLoading}
        className={`w-full ${
          !hold || holdLoading
            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
            : theme.buttonPrimary
        } font-semibold py-3 rounded-lg transition shadow-md`}
      >
        {holdLoading ? 'Processing...' : hold ? 'Reserve' : 'Select a date to continue'}
      </button>

      <div className="text-center text-sm text-gray-600 mt-3">
        {hold ? "You won't be charged yet" : 'Choose your dates to get started'}
      </div>

      <div className="mt-4 pt-4 border-t space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">
            {formatCentsToDollars(pricePerPerson)} x {guests} guest{guests > 1 ? 's' : ''}
          </span>
          <span className="font-medium">{formatCentsToDollars(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-600">Service fee</span>
          <span className="font-medium">{formatCentsToDollars(serviceFee)}</span>
        </div>
        <div className="pt-3 border-t flex justify-between font-semibold">
          <span>Total</span>
          <span>{formatCentsToDollars(totalPrice)}</span>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900">
              <div className="font-semibold mb-1">Booking Protection</div>
              <div>Full refund if cancelled 7+ days before trip</div>
            </div>
          </div>
        </div>
      </div>

      {/* Hold Expiration Modals */}
      <StillInterestedModal
        isOpen={showStillInterestedModal}
        onExtend={handleExtendFromModal}
        onRelease={handleReleaseFromModalWithClear}
        extensionMinutes={5}
      />
      <HoldExpiredModal
        isOpen={showExpiredModal}
        onReload={handleReloadFromExpired}
        onBrowse={handleContinueBrowsing}
      />
    </div>
  );
});

export default BookingCard;
